import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Search, Plus, Edit, Key, Trash2, UserX, UserCheck, Smartphone, RefreshCw, Copy, Eye, EyeOff, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/admin';
import { Button, Select, Badge, Modal, Card } from '@/components/common';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { users as usersApi } from '@/utils/api';
import { toast } from 'sonner';
import { useUsers } from '@/hooks/useUsers';
import {
  createUserSchema, editUserSchema, resetPasswordSchema,
  type CreateUserInput, type EditUserInput, type ResetPasswordInput,
} from '@/schemas/user';

const ROLE_BADGE_VARIANTS = {
  admin: 'purple',
  büro: 'info',
  bauleiter: 'success',
  mitarbeiter: 'default',
  pending: 'warning',
};

const ROLE_LABELS = {
  admin: 'Admin',
  büro: 'Büro',
  bauleiter: 'Bauleiter',
  mitarbeiter: 'Mitarbeiter',
  pending: 'Ausstehend',
};

const ROLE_OPTIONS = [
  { value: 'mitarbeiter', label: 'Mitarbeiter' },
  { value: 'bauleiter', label: 'Bauleiter' },
  { value: 'büro', label: 'Büro' },
  { value: 'admin', label: 'Admin' },
];

const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'Alle Rollen' },
  { value: 'mitarbeiter', label: 'Mitarbeiter' },
  { value: 'bauleiter', label: 'Bauleiter' },
  { value: 'büro', label: 'Büro' },
  { value: 'admin', label: 'Admin' },
  { value: 'pending', label: 'Ausstehend' },
];

const EMPLOYMENT_OPTIONS = [
  { value: 'VZ', label: 'Vollzeit' },
  { value: 'TZ', label: 'Teilzeit' },
];

function generatePassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '@?!#*';
  const all = upper + lower + digits;
  // 7 Zeichen Kern (mix aus Groß, Klein, Ziffern)
  const core = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    ...Array.from({ length: 4 }, () => all[Math.floor(Math.random() * all.length)]),
  ].sort(() => Math.random() - 0.5);
  // Sonderzeichen vorne oder hinten
  const s = special[Math.floor(Math.random() * special.length)];
  return Math.random() < 0.5 ? s + core.join('') : core.join('') + s;
}

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Alle Status' },
  { value: 'active', label: 'Aktiv' },
  { value: 'pending', label: 'Pending' },
  { value: 'inactive', label: 'Deaktiviert' },
];

// Separate small component so it can hold its own local state without polluting editForm schema
function AppAccessSetup({
  editModal,
  invalidateUsers,
  setEditModal,
}: {
  editModal: { open: boolean; user: any };
  invalidateUsers: () => void;
  setEditModal: (v: { open: boolean; user: any }) => void;
}) {
  const [appUsername, setAppUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
      <p className="text-sm text-gray-600">Kein App-Zugang eingerichtet. Vergeben Sie eine Benutzernummer und ein Passwort.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">App-Benutzernummer</label>
          <Input value={appUsername} onChange={(e) => setAppUsername(e.target.value)} placeholder="z.B. 1001" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">App-Passwort</label>
          <Input type="password" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} placeholder="min. 6 Zeichen" />
        </div>
      </div>
      <Button
        variant="success"
        size="sm"
        icon={Smartphone}
        disabled={!appUsername.trim() || !appPassword.trim() || appPassword.length < 6}
        onClick={() => {
          usersApi.setAppCredentials(editModal.user.id, {
            appUsername: appUsername.trim(),
            appPassword: appPassword.trim(),
          }).then(() => { alert('App-Zugang eingerichtet'); setEditModal({ open: false, user: null }); invalidateUsers(); })
            .catch((e: any) => alert(e.message));
        }}
      >
        App-Zugang einrichten
      </Button>
    </div>
  );
}

const Benutzerverwaltung = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [passwordModal, setPasswordModal] = useState({ open: false, user: null });
  const [createChatAccount, setCreateChatAccount] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });

  // Form state — React Hook Form
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  const createForm = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { username: '', password: '', name: '', email: '', phone: '', role: 'mitarbeiter' },
  });

  const editForm = useForm<EditUserInput>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      username: '', name: '', email: '', phone: '', birthdate: '',
      role: 'mitarbeiter', is_active: true, user_status: 'active', exit_date: '',
      show_in_overview: true, webui_access: true, personal_number: '',
      employment_type: 'VZ', vacation_days_total: 30, vacation_days_remaining: 30,
      vacation_days_carried: 0, work_start_time: '07:00', work_end_time: '16:00',
      default_break_minutes: 60,
    },
  });

  const passwordForm = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '' },
  });

  const { data: users = [], isLoading: loading } = useUsers(
    roleFilter !== 'all' ? { role: roleFilter } : undefined
  );

  const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  // Filter (sorting handled by DataTable)
  const filteredUsers = users.filter((user) => {
    // Status filter
    if (statusFilter !== 'all') {
      const userStatus = user.user_status || (user.is_active ? 'active' : 'inactive');
      if (userStatus !== statusFilter) return false;
    }
    // Search filter
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      user.name?.toLowerCase().includes(s) ||
      user.username?.toLowerCase().includes(s)
    );
  });

  // Column definitions for DataTable
  const columns: ColumnDef<(typeof users)[0]>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{row.original.name}</span>
          {!row.original.show_in_overview && (
            <span title="Nicht in Mitarbeiter-Übersicht" className="text-gray-400 text-xs">
              (versteckt)
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'username',
      header: 'Benutzername',
      cell: ({ row }) => (
        <span className="text-gray-600">{row.original.username}</span>
      ),
    },
    {
      accessorKey: 'role',
      header: 'Rolle',
      cell: ({ row }) => (
        <Badge variant={ROLE_BADGE_VARIANTS[row.original.role] || 'default'}>
          {ROLE_LABELS[row.original.role] || row.original.role}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => {
        const status = row.original.user_status || (row.original.is_active ? 'active' : 'inactive');
        if (status === 'active') return <Badge variant="success">Aktiv</Badge>;
        if (status === 'pending') return <Badge variant="warning">Pending</Badge>;
        return <Badge variant="danger">Deaktiviert</Badge>;
      },
    },
    {
      id: 'actions',
      header: () => 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              icon={Edit}
              onClick={(e) => { e.stopPropagation(); openEditModal(user); }}
            >
              Bearbeiten
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={Key}
              onClick={(e) => { e.stopPropagation(); passwordForm.reset(); setPasswordModal({ open: true, user }); }}
            >
              Passwort
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={(user.user_status || 'active') === 'inactive' ? UserCheck : UserX}
              onClick={(e) => { e.stopPropagation(); handleToggleActive(user); }}
            >
              {(user.user_status || 'active') === 'inactive' ? 'Aktivieren' : 'Deaktivieren'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={FileText}
              onClick={(e) => { e.stopPropagation(); navigate(`/admin/mitarbeiter/${user.id}?tab=stammblatt`); }}
            >
              Stammblatt
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={Trash2}
              onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, user }); }}
              className="text-red-600 hover:text-red-700"
            >
              Löschen
            </Button>
          </div>
        );
      },
    },
  ];

  // Create user
  const createMutation = useMutation({
    mutationFn: (payload: CreateUserInput) => usersApi.create({
      username: payload.username.trim(),
      password: payload.password,
      name: payload.name.trim(),
      email: payload.email?.trim() || undefined,
      phone: payload.phone?.trim() || undefined,
      role: payload.role,
    }),
    onSuccess: ({ data: result }) => {
      if (result.nextcloud?.success) {
        toast.success('Benutzer erstellt – Nextcloud-Account wurde automatisch angelegt');
      } else if (result.nextcloud?.error) {
        toast.success(`Benutzer erstellt – Nextcloud-Account konnte nicht angelegt werden: ${result.nextcloud.error}`);
      } else {
        toast.success('Benutzer erfolgreich erstellt');
      }
      if (result.mattermost?.success) {
        toast.success('Chat-Zugang (Mattermost) wurde angelegt');
      } else if (result.mattermost?.error && !result.mattermost?.skipped) {
        toast.warning(`Chat-Zugang (Mattermost) konnte nicht angelegt werden: ${result.mattermost.error}`);
      }
      setCreateModal(false);
      createForm.reset();
      invalidateUsers();
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Erstellen des Benutzers'),
  });

  const handleCreate = createForm.handleSubmit((data) => {
    createMutation.mutate(data);
  });

  // Open edit modal
  const openEditModal = (user: any) => {
    editForm.reset({
      username: user.username || '',
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      birthdate: user.birthdate || '',
      role: user.role || 'mitarbeiter',
      is_active: user.is_active !== false,
      user_status: user.user_status || (user.is_active !== false ? 'active' : 'inactive'),
      exit_date: user.exit_date || '',
      show_in_overview: user.show_in_overview !== false,
      webui_access: user.webui_access !== false,
      personal_number: user.personal_number || '',
      employment_type: user.employment_type || 'VZ',
      vacation_days_total: user.vacation_days_total ?? 30,
      vacation_days_remaining: user.vacation_days_remaining ?? 30,
      vacation_days_carried: user.vacation_days_carried ?? 0,
      work_start_time: user.work_start_time || '07:00',
      work_end_time: user.work_end_time || '16:00',
      default_break_minutes: user.default_break_minutes ?? 60,
    });
    setEditModal({ open: true, user });
  };

  // Save edit
  const editMutation = useMutation({
    mutationFn: ({ id, data }: { id: any; data: any }) => usersApi.update(id, data),
    onSuccess: () => {
      toast.success('Benutzer erfolgreich aktualisiert');
      setEditModal({ open: false, user: null });
      invalidateUsers();
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Aktualisieren'),
  });

  const handleEdit = editForm.handleSubmit((data) => {
    editMutation.mutate({
      id: editModal.user.id,
      data: {
        username: data.username.trim(),
        name: data.name.trim(),
        email: data.email?.trim() || null,
        phone: data.phone?.trim() || null,
        birthdate: data.birthdate || null,
        role: data.role,
        is_active: data.user_status !== 'inactive',
        user_status: data.user_status,
        exit_date: data.exit_date || null,
        show_in_overview: data.show_in_overview,
        webui_access: data.webui_access,
        personal_number: data.personal_number?.trim() || null,
        employment_type: data.employment_type,
        vacation_days_total: Number(data.vacation_days_total),
        vacation_days_remaining: Number(data.vacation_days_remaining),
        vacation_days_carried: Number(data.vacation_days_carried),
        work_start_time: data.work_start_time || null,
        work_end_time: data.work_end_time || null,
        default_break_minutes: Number(data.default_break_minutes),
      },
    });
  });

  // Reset password
  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password, createMattermost }: { id: any; password: string; createMattermost: boolean }) =>
      usersApi.resetPassword(id, password, createMattermost),
    onSuccess: ({ data: result }, { createMattermost }) => {
      toast.success('Passwort erfolgreich zurückgesetzt');
      if (createMattermost) {
        if (result.mattermost?.success) toast.success(`Chat-Zugang bereit – Benutzername im Chat: ${result.mattermost.username}`);
        else toast.warning(`Chat-Zugang (Mattermost) konnte nicht angelegt werden: ${result.mattermost?.error || 'unbekannter Fehler'}`);
      }
      setPasswordModal({ open: false, user: null });
      setCreateChatAccount(false);
      passwordForm.reset();
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Zurücksetzen des Passworts'),
  });

  const handleResetPassword = passwordForm.handleSubmit((data) => {
    resetPasswordMutation.mutate({ id: passwordModal.user.id, password: data.password, createMattermost: createChatAccount });
  });

  // Toggle active
  const toggleActiveMutation = useMutation({
    mutationFn: ({ user, newStatus }: { user: any; newStatus: string }) =>
      usersApi.update(user.id, {
        is_active: newStatus === 'active',
        user_status: newStatus,
        exit_date: newStatus === 'inactive' ? new Date().toISOString().slice(0, 10) : null,
      }),
    onSuccess: (_, { newStatus }) => {
      toast.success(newStatus === 'active' ? 'Benutzer aktiviert' : 'Benutzer deaktiviert');
      invalidateUsers();
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Ändern des Status'),
  });

  const handleToggleActive = (user: any) => {
    const isInactive = (user.user_status || 'active') === 'inactive';
    const newStatus = isInactive ? 'active' : 'inactive';
    toggleActiveMutation.mutate({ user, newStatus });
  };

  // Delete
  const deleteMutation = useMutation({
    mutationFn: (id: any) => usersApi.delete(id),
    onSuccess: () => {
      toast.success('Benutzer erfolgreich gelöscht');
      setDeleteModal({ open: false, user: null });
      invalidateUsers();
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Löschen des Benutzers'),
  });

  const handleDelete = () => {
    deleteMutation.mutate(deleteModal.user.id);
  };

  return (
    <div>
      <PageHeader
        title="Benutzerverwaltung"
        description="Benutzer erstellen, bearbeiten und verwalten"
      />

      {/* Filters + Create Button */}
      <Card className="mb-6" padding={false}>
        <div className="flex flex-wrap gap-4 px-4 py-3">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Suche..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            options={ROLE_FILTER_OPTIONS}
            placeholder=""
            className="w-48"
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={STATUS_FILTER_OPTIONS}
            placeholder=""
            className="w-48"
          />
          <div className="ml-auto">
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setCreateModal(true)}
            >
              Neuer Benutzer
            </Button>
          </div>
        </div>
      </Card>

      {/* User Table */}
      {loading ? (
        <div className="flex justify-center py-12 text-gray-400">Laden…</div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredUsers}
          emptyText={search ? 'Keine Benutzer gefunden' : 'Keine Benutzer vorhanden'}
        />
      )}

      {/* Create User Modal */}
      <Modal
        isOpen={createModal}
        onClose={() => {
          setCreateModal(false);
          createForm.reset();
          setShowCreatePassword(false);
        }}
        title="Neuer Benutzer"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCreateModal(false);
                createForm.reset();
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              loading={createMutation.isPending}
            >
              Erstellen
            </Button>
          </>
        }
      >
        <Form {...createForm}>
          <form className="space-y-4" onSubmit={handleCreate}>
            <FormField
              control={createForm.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Benutzername <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z.B. jdoe oder 9001" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={createForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Passwort <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          {...field}
                          type={showCreatePassword ? 'text' : 'password'}
                          placeholder="Mindestens 8 Zeichen"
                          className="pr-10 font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCreatePassword(!showCreatePassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          title={showCreatePassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                        >
                          {showCreatePassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const pw = generatePassword();
                          createForm.setValue('password', pw);
                          setShowCreatePassword(true);
                        }}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 flex items-center gap-1 whitespace-nowrap"
                        title="Passwort generieren"
                      >
                        <RefreshCw size={14} />
                        Generieren
                      </button>
                      {field.value && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(field.value);
                            toast.success('Passwort in Zwischenablage kopiert');
                          }}
                          className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 flex items-center gap-1"
                          title="Passwort kopieren"
                        >
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </FormControl>
                  {field.value && (
                    <p className="text-xs text-gray-500">Dieses Passwort gilt auch für Nextcloud.</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={createForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Anzeigename <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Vor- und Nachname" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={createForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-Mail</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="optional" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefonnummer</FormLabel>
                    <FormControl>
                      <Input {...field} type="tel" placeholder="optional" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={createForm.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rolle</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, user: null })}
        title={`Benutzer bearbeiten: ${editModal.user?.name || ''}`}
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setEditModal({ open: false, user: null })}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={handleEdit}
              loading={editMutation.isPending}
            >
              Speichern
            </Button>
          </>
        }
      >
        <Form {...editForm}>
          <form className="space-y-4" onSubmit={handleEdit}>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={editForm.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Login-Benutzername</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={editForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-Mail</FormLabel>
                    <FormControl><Input {...field} type="email" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefonnummer</FormLabel>
                    <FormControl><Input {...field} type="tel" placeholder="optional" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="birthdate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Geburtsdatum</FormLabel>
                    <FormControl><Input {...field} type="date" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={editForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rolle</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="user_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          field.onChange(newStatus);
                          editForm.setValue('is_active', newStatus !== 'inactive');
                          if (newStatus === 'inactive' && !editForm.getValues('exit_date')) {
                            editForm.setValue('exit_date', new Date().toISOString().slice(0, 10));
                          }
                          if (newStatus === 'active') {
                            editForm.setValue('exit_date', '');
                          }
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Aktiv</option>
                        <option value="pending">Pending</option>
                        <option value="inactive">Deaktiviert</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {editForm.watch('user_status') === 'inactive' && (
              <FormField
                control={editForm.control}
                name="exit_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Austrittsdatum</FormLabel>
                    <FormControl><Input {...field} type="date" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <div className="space-y-2 py-2">
              <FormField
                control={editForm.control}
                name="show_in_overview"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <div>
                      <span className="text-sm font-medium text-gray-700">In Mitarbeiter-Übersicht anzeigen</span>
                      <p className="text-xs text-gray-500">Wenn deaktiviert, erscheint der Benutzer nicht in der Mitarbeiter-Liste und im Stundenzettel</p>
                    </div>
                  </div>
                )}
              />
              <FormField
                control={editForm.control}
                name="webui_access"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Zugriff WebUI</span>
                      <p className="text-xs text-gray-500">Wenn deaktiviert, kann sich der Benutzer nicht in der WebUI anmelden (App-Zugang bleibt aktiv)</p>
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={editForm.control}
                name="personal_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Personalnummer</FormLabel>
                    <FormControl><Input {...field} placeholder="optional" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="employment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beschäftigungsart</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val);
                          if (val === 'TZ') {
                            editForm.setValue('work_end_time', '11:00');
                            editForm.setValue('default_break_minutes', 0);
                          } else {
                            editForm.setValue('work_end_time', '16:00');
                            editForm.setValue('default_break_minutes', 60);
                          }
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {EMPLOYMENT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={editForm.control}
                name="vacation_days_total"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Jahresanspruch</FormLabel>
                    <FormControl><Input {...field} type="number" min={0} max={50} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="vacation_days_remaining"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Resturlaub</FormLabel>
                    <FormControl><Input {...field} type="number" min={0} max={50} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="vacation_days_carried"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Übertrag Vorjahr</FormLabel>
                    <FormControl><Input {...field} type="number" min={0} max={50} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={editForm.control}
                name="work_start_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Arbeitsbeginn</FormLabel>
                    <FormControl><Input {...field} type="time" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="work_end_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Arbeitsende</FormLabel>
                    <FormControl><Input {...field} type="time" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="default_break_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pause (Min.)</FormLabel>
                    <FormControl><Input {...field} type="number" min={0} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* App-Zugang */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                PWA-Zugang
              </h4>
              {editModal.user?.app_username ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-600">App-Benutzernummer</p>
                      <p className="text-lg font-mono font-bold text-green-800">{editModal.user.app_username}</p>
                    </div>
                    <Badge variant="success">App aktiv</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={Key}
                      onClick={() => {
                        const pw = prompt('Neues App-Passwort (min. 6 Zeichen):');
                        if (!pw || pw.length < 6) { if (pw) alert('Passwort muss mindestens 6 Zeichen haben'); return; }
                        usersApi.setAppCredentials(editModal.user.id, {
                          appUsername: editModal.user.app_username,
                          appPassword: pw,
                        }).then(() => { alert('App-Passwort geändert'); invalidateUsers(); }).catch(e => alert(e.message));
                      }}
                    >
                      App-Passwort ändern
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      icon={UserX}
                      onClick={() => {
                        if (!confirm(`App-Zugang für ${editModal.user.name} wirklich widerrufen?`)) return;
                        usersApi.revokeAppAccess(editModal.user.id)
                          .then(() => { alert('App-Zugang widerrufen'); setEditModal({ open: false, user: null }); invalidateUsers(); })
                          .catch(e => alert(e.message));
                      }}
                    >
                      App-Zugang widerrufen
                    </Button>
                  </div>
                </div>
              ) : (
                <AppAccessSetup editModal={editModal} invalidateUsers={invalidateUsers} setEditModal={setEditModal} />
              )}
            </div>
          </form>
        </Form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        isOpen={passwordModal.open}
        onClose={() => {
          setPasswordModal({ open: false, user: null }); setCreateChatAccount(false);
          passwordForm.reset();
        }}
        title={`Passwort zurücksetzen: ${passwordModal.user?.name || ''}`}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setPasswordModal({ open: false, user: null }); setCreateChatAccount(false);
                passwordForm.reset();
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={handleResetPassword}
              loading={resetPasswordMutation.isPending}
            >
              Passwort setzen
            </Button>
          </>
        }
      >
        <Form {...passwordForm}>
          <form onSubmit={handleResetPassword}>
            <FormField
              control={passwordForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Neues Passwort</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" placeholder="Mindestens 8 Zeichen" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <label className="mt-4 flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={createChatAccount}
                onChange={(e) => setCreateChatAccount(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Chat-Zugang (Mattermost) einrichten</span>
                <p className="text-xs text-gray-500">Legt das Chat-Konto an, falls noch keins existiert. Das neue Passwort gilt dann auch für den Chat (chat.compeer-gmbh.de).</p>
              </div>
            </label>
          </form>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, user: null })}
        title="Benutzer löschen"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDeleteModal({ open: false, user: null })}
            >
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Endgültig löschen
            </Button>
          </>
        }
      >
        <p className="text-gray-700">
          Möchten Sie den Benutzer <strong>{deleteModal.user?.name}</strong> ({deleteModal.user?.username}) wirklich löschen?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
      </Modal>
    </div>
  );
};

export default Benutzerverwaltung;
